import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useI18n } from "../../../hooks/useTranslations";
import { useClaimTypesApi } from "../../../http/claimTypes/api";
import { UpsertClaimType } from "../../../http/claimTypes/types";
import ModalDialog from "../../ui/ModalDialog";
import ClaimTypeForm, { ClaimTypeFormBody } from "./ClaimTypeForm";

type EditClaimTypeDialogProps = {
  claimTypeId: string;
  onClose: () => void;
};

const EditClaimTypeDialog: FC<EditClaimTypeDialogProps> = ({ claimTypeId, onClose }) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const claimTypesApi = useClaimTypesApi();

  const claimTypeSwr = claimTypesApi.getClaimType(claimTypeId, aborter.current.signal);
  const [claimType, isInitialized] = useDataInitializer(claimTypeSwr.data, claimTypeSwr.error);

  useEffect(() => {
    claimTypeId && claimTypeSwr.trigger();
  }, [claimTypeId]);

  useEffect(() => {
    claimTypeSwr.error && dispatch(showErrorNotification(i18n, claimTypeSwr.error));
  }, [claimTypeSwr.error]);

  const onSubmit = async (formData: ClaimTypeFormBody): Promise<void> => {
    if (claimType?.id) {
      setIsSubmitting(true);

      const body: UpsertClaimType = {
        name: formData.name,
      };

      try {
        await claimTypesApi.updateAdminClaimType(claimType.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.claimTypes.edit}>
      <ClaimTypeForm
        onSubmit={onSubmit}
        isDisabled={!userSettings.permissions.claimTypes.update || isSubmitting || !claimType?.id}
        isLoading={!isInitialized}
        claimType={claimType}
      />
    </ModalDialog>
  );
};

export default EditClaimTypeDialog;
